import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Home, Plus, BadgeCheck, Ticket, Edit2 } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

export default function CheckoutPage() {
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [cart, setCart] = useState([]);
  const [voucherDiscount, setVoucherDiscount] = useState(0);
  const [appliedVoucher, setAppliedVoucher] = useState(null);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [refId, setRefId] = useState("");
  const [showRefInput, setShowRefInput] = useState(false);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);

  const [addressForm, setAddressForm] = useState({
    label: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    country: "India",
    id: null,
  });

  const navigate = useNavigate();
  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("token");
  const API_URL = userId
    ? `https://accessoriesgirly.onrender.com/api/users/${userId}`
    : null;



// inside your component
const fetchAddresses = useCallback(async () => {
  if (!userId || !API_URL) return;
  try {
    const res = await axios.get(API_URL);
    setAddresses(res.data.user.addresses || []);
  } catch (err) {
    console.error(err);
  }
}, [API_URL, userId]);

useEffect(() => {
  // Load cart
  const savedCart = JSON.parse(localStorage.getItem("cart")) || [];
  setCart(
    savedCart.map((item) => ({
      ...item,
      price: Number(item.price) || 0,
      quantity: Number(item.quantity) || 1,
    }))
  );

  // Load voucher
  const savedVoucher = JSON.parse(localStorage.getItem("voucher"));
  if (savedVoucher) {
    setVoucherDiscount(savedVoucher.amount);
    setAppliedVoucher(savedVoucher.code);
  }

  // Fetch addresses
  fetchAddresses();
}, [fetchAddresses]);


  const openAddressModal = (addr = null) => {
    if (addr) {
      setAddressForm({ ...addr, id: addr._id });
    } else {
      setAddressForm({
        label: "",
        street: "",
        city: "",
        state: "",
        zip: "",
        country: "India",
        id: null,
      });
    }
    setIsAddressModalOpen(true);
  };

  const handleAddressSubmit = async () => {
    try {
      let updatedAddresses;
      if (addressForm.id) {
        updatedAddresses = addresses.map((addr) =>
          addr._id === addressForm.id ? { ...addressForm } : addr
        );
      } else {
        updatedAddresses = [...addresses, { ...addressForm }];
      }

      const res = await axios.put(API_URL, { addresses: updatedAddresses });
      setAddresses(res.data.user.addresses);
      setIsAddressModalOpen(false);
      setAddressForm({
        label: "",
        street: "",
        city: "",
        state: "",
        zip: "",
        country: "India",
        id: null,
      });
    } catch (err) {
      console.error(err);
      alert("Failed to save address");
    }
  };

  const total = cart.reduce(
    (sum, item) =>
      sum + (Number(item.price) || 0) * (Number(item.quantity) || 1),
    0
  );
  const finalTotal = Math.max(total - voucherDiscount, 0);
  const savedAmount = voucherDiscount;

  const handleOrder = () => {
    if (!selectedAddress) return;
    setIsPaymentModalOpen(true);
  };

  const handlePlaceOrder = async () => {
    if (!refId.trim()) {
      alert("Please enter your Payment Reference ID");
      return;
    }

    try {
      setIsPlacingOrder(true);
      const orderData = {
        userId,
        address: selectedAddress,
        items: cart.map((item) => ({
          product: item._id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          image: item.image,
        })),
        totalAmount: total,
        discount: voucherDiscount,
        finalAmount: finalTotal,
        voucher: appliedVoucher
          ? { code: appliedVoucher, amount: voucherDiscount }
          : null,
        refId,
      };

      const res = await axios.post(
        "https://accessoriesgirly.onrender.com/api/orders",
        orderData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      alert("✅ " + res.data.message);

      localStorage.removeItem("cart");
      localStorage.removeItem("voucher");
      setCart([]);
      setAppliedVoucher(null);
      setVoucherDiscount(0);
      setIsPaymentModalOpen(false);
      setRefId("");
      setShowRefInput(false);
      navigate("/my-orders");
    } catch (err) {
      console.error(err);
      alert(
        "❌ Failed to place order: " +
          (err.response?.data?.message || err.message)
      );
    } finally {
      setIsPlacingOrder(false);
    }
  };

  if (!userId)
    return (
      <div className="text-center py-20">
        <p className="mb-4 text-lg font-medium text-gray-700">
          Please Login First
        </p>
        <button
          onClick={() => navigate("/login")}
          className="px-6 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition"
        >
          Go to Login
        </button>
      </div>
    );

  return (
    <section className="py-12 bg-pink-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-pink-600 mb-8 text-center">
          Checkout
        </h1>

        <div className="flex flex-col lg:flex-row gap-10">
          <div className="lg:w-2/3 w-full bg-white p-6 rounded-xl shadow-md">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Select Address
            </h2>

            {addresses.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">No address found</p>
                <button
                  onClick={() => openAddressModal()}
                  className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 flex items-center gap-2 mx-auto"
                >
                  <Plus className="w-5 h-5" /> Add your first address
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {addresses.map((addr) => (
                  <motion.div
                    key={addr._id}
                    className={`flex items-center justify-between border p-4 rounded-lg cursor-pointer ${
                      selectedAddress?._id === addr._id
                        ? "border-pink-500 bg-pink-50"
                        : "hover:bg-gray-50"
                    }`}
                    onClick={() => setSelectedAddress(addr)}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        checked={selectedAddress?._id === addr._id}
                        readOnly
                      />
                      <div>
                        <p className="font-semibold flex items-center gap-2">
                          <Home className="w-5 h-5 text-pink-600" />
                          {addr.label}
                        </p>
                        <p className="text-gray-600 text-sm">
                          {`${addr.street}, ${addr.city}, ${
                            addr.state || ""
                          }, ${addr.zip || ""}, ${addr.country}`}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openAddressModal(addr);
                      }}
                      className="text-pink-600 hover:text-pink-800"
                    >
                      <Edit2 size={18} />
                    </button>
                  </motion.div>
                ))}

                <button
                  onClick={() => openAddressModal()}
                  className="mt-4 px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" /> Add New Address
                </button>
              </div>
            )}
          </div>

          <div className="lg:w-1/3 w-full bg-white p-6 rounded-xl shadow-md">
            <h2 className="text-2xl font-bold text-pink-600 mb-4">
              Order Summary
            </h2>

            <div className="flex justify-between mb-2">
              <span className="text-gray-700">Subtotal:</span>
              <span className="font-semibold">₹{total.toFixed(2)}</span>
            </div>

            {appliedVoucher && (
              <div className="flex justify-between mb-2 items-center">
                <span className="text-gray-700 flex items-center gap-1">
                  <Ticket className="w-4 h-4 text-pink-600" />
                  Voucher ({appliedVoucher})
                </span>
                <span className="font-semibold text-green-600">
                  -₹{voucherDiscount.toFixed(2)}
                </span>
              </div>
            )}

            {savedAmount > 0 && (
              <p className="text-green-600 font-semibold mt-2 text-center flex items-center justify-center gap-2">
                <BadgeCheck className="w-5 h-5" />
                Congrats! You saved ₹{savedAmount.toFixed(2)}
              </p>
            )}

            <div className="border-t border-gray-200 mt-2 pt-2 flex justify-between text-xl font-bold">
              <span>Total:</span>
              <span>₹{finalTotal.toFixed(2)}</span>
            </div>

            <button
              className={`mt-6 w-full py-3 rounded-lg transition-colors ${
                selectedAddress
                  ? "bg-pink-600 text-white hover:bg-pink-700"
                  : "bg-gray-300 text-gray-600 cursor-not-allowed"
              }`}
              disabled={!selectedAddress}
              onClick={handleOrder}
            >
              Order Now
            </button>
          </div>
        </div>
      </div>

      {isAddressModalOpen && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <motion.div
            className="bg-white rounded-xl p-6 w-full max-w-md relative"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
          >
            <h2 className="text-xl font-bold text-pink-600 mb-4">
              {addressForm.id ? "Edit Address" : "Add Address"}
            </h2>
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Label (Home/Work)"
                value={addressForm.label}
                onChange={(e) =>
                  setAddressForm({ ...addressForm, label: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Street"
                value={addressForm.street}
                onChange={(e) =>
                  setAddressForm({ ...addressForm, street: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="City"
                value={addressForm.city}
                onChange={(e) =>
                  setAddressForm({ ...addressForm, city: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="State"
                value={addressForm.state}
                onChange={(e) =>
                  setAddressForm({ ...addressForm, state: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="ZIP Code"
                value={addressForm.zip}
                onChange={(e) =>
                  setAddressForm({ ...addressForm, zip: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Country"
                value={addressForm.country}
                onChange={(e) =>
                  setAddressForm({ ...addressForm, country: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:outline-none"
              />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setIsAddressModalOpen(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleAddressSubmit}
                className="px-4 py-2 rounded-lg bg-pink-600 text-white hover:bg-pink-700"
              >
                Save
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {isPaymentModalOpen && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <motion.div
            className="bg-white rounded-xl p-6 w-full max-w-3xl relative grid grid-cols-1 md:grid-cols-2 gap-6 shadow-lg"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
          >
            <button
              onClick={() => setIsPaymentModalOpen(false)}
              className="absolute top-3 right-3 text-gray-600 hover:text-red-500 transition"
            >
              ✖
            </button>

            <div className="flex flex-col items-center justify-center text-center">
              <img
                src="https://ik.imagekit.io/rasoipro212/WhatsApp%20Image%202025-09-14%20at%2019.48.55.jpeg?updatedAt=1757860611774"
                alt="QR Code"
                className="w-48 h-48 md:w-64 md:h-64 object-contain rounded-lg border"
              />
              <p className="mt-2 text-sm text-gray-600">
                Scan this QR with <b>PhonePe / GPay / Paytm</b>
              </p>
            </div>

            <div className="flex flex-col justify-between">
              <div>
                <h2 className="text-xl font-bold text-pink-600 mb-3 text-center md:text-left">
                  Payment Instructions
                </h2>
                <ul className="list-disc ml-5 text-gray-700 space-y-2 text-sm md:text-base">
                  <li>
                    Open <b>PhonePe / GPay / Paytm</b> on your phone.
                  </li>
                  <li>Scan the QR code on the left.</li>
                  <li>
                    Enter the exact amount: <b>₹{finalTotal.toFixed(2)}</b>
                  </li>
                  <li>
                    After payment, click <b>"I Have Paid"</b>.
                  </li>
                </ul>
              </div>

              {!showRefInput ? (
                <button
                  onClick={() => setShowRefInput(true)}
                  className="mt-6 w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                >
                  ✅ I Have Paid
                </button>
              ) : (
                <div className="mt-6 space-y-3">
                  <input
                    type="text"
                    placeholder="Enter Payment Reference ID"
                    value={refId}
                    onChange={(e) => setRefId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:outline-none text-sm md:text-base"
                  />
                  <button
                    onClick={handlePlaceOrder}
                    disabled={isPlacingOrder}
                    className={`w-full py-2 rounded-lg transition ${
                      isPlacingOrder
                        ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                        : "bg-pink-600 text-white hover:bg-pink-700"
                    }`}
                  >
                    {isPlacingOrder ? "Placing Order..." : "🎉 Place Order"}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </section>
  );
}